import { ID, NewVision, Vision } from '@griply/types';
import { withRoles } from '@griply/utils';

import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';

export type CreateVisionOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TDb = FirestoreInstance,
> = {
  vision: NewVision;
  userId: ID;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: string };
  setDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void> | void;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  onWriteError: (error: unknown) => void;
};

export const createVision = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TDb = FirestoreInstance,
>({
  vision,
  userId,
  collection,
  doc,
  setDoc,
  db,
  serverTimestamp,
  onWriteError,
}: CreateVisionOptions<
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TDb
>): Promise<Vision> => {
  const collectionRef = collection(db, FirestoreCollections.Visions);
  const newDoc = doc(collectionRef);

  const newDocData = withRoles(
    {
      id: newDoc.id,
      description: vision.description ?? '',
      images: vision.images ?? [],
      lifeAreaId: vision.lifeAreaId ?? null,
      frozenAt: null,
    },
    userId,
  );

  detachWrite(
    setDoc(newDoc, {
      ...newDocData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }),
    onWriteError,
  );

  return {
    ...newDocData,
    createdAt: new Date(),
  };
};
